import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import '../style/consulting.css';
import GoogleCalendarEmbed from '../components/GoogleCalendarEmbed';
import Seo from '../components/Seo';

function Consulting() {
  const { t, i18n } = useTranslation();

  // Language-specific booking link for the free discovery session
  const discoveryBookingLinks = {
    fr: 'https://calendar.google.com/calendar/appointments/schedules/AcZssZ2kOcle5PKC-uVqi9TonjvIkh0ZPFhyBeejGnhsLc1Eyl7H5nOxteaOv1uNkA7LrR91UDrOOdaw?gv=true',
    en: 'https://calendar.google.com/calendar/appointments/schedules/AcZssZ0FQP56_LHYLy-lY5dSOw9sez8DTr-BZQTpEkmineXcKTjrLuMNvsQVpp2Uj6bIwFuqcfDXCHqG?gv=true'
  };

  const currentLang = i18n.language && i18n.language.startsWith('en') ? 'en' : 'fr';

  const scrollToBooking = () => {
    const el = document.getElementById('discovery-booking');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const discoveryFeatures = ['f1', 'f2', 'f3'];
  const diagnosticFeatures = ['f1', 'f2', 'f3', 'f4'];

  return (
    <section id="consulting" className="consulting-page">
      <Seo path='/consulting' titleKey='seo.consulting.title' descriptionKey='seo.consulting.description' />
      <Container>
        <div className="title-holder text-center">
          <h2>{t('consulting.title')}</h2>
          <div className="subtitle">{t('consulting.subtitle')}</div>
        </div>

        <div className="consulting-intro">
          <Row>
            <Col lg={8} className="mx-auto">
              <p className="lead text-center">{t('consulting.intro')}</p>
            </Col>
          </Row>
        </div>

        <div className="packages-section">
          <h3 className="section-title text-center">{t('consulting.startTitle')}</h3>
          <Row className="justify-content-center steps-row">
            {/* Step 1 — Free discovery session */}
            <Col md={6} lg={5} className="mb-4">
              <Card className="package-card step-card h-100">
                <Card.Body>
                  <Badge bg="success">{t('consulting.steps.discovery.badge')}</Badge>
                  <Card.Title>{t('consulting.steps.discovery.title')}</Card.Title>
                  <div className="price">
                    <span className="free">{t('consulting.free')}</span>
                  </div>
                  <div className="time-duration">
                    <i className="far fa-clock"></i> {t('consulting.steps.discovery.duration')}
                  </div>
                  <hr />
                  <ul className="feature-list">
                    {discoveryFeatures.map((f) => (
                      <li key={f}>
                        <i className="fas fa-check"></i> {t(`consulting.steps.discovery.features.${f}`)}
                      </li>
                    ))}
                  </ul>
                  <Button variant="outline-primary" className="w-100 mt-3" onClick={scrollToBooking}>
                    {t('consulting.steps.discovery.cta')}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            {/* Step 2 — Automation diagnostic (paid, highlighted) */}
            <Col md={6} lg={5} className="mb-4">
              <Card className="package-card step-card featured h-100">
                <Card.Body>
                  <Badge bg="primary">{t('consulting.steps.diagnostic.badge')}</Badge>
                  <Card.Title>{t('consulting.steps.diagnostic.title')}</Card.Title>
                  <div className="price">
                    <span className="amount">{t('consulting.steps.diagnostic.price')}</span>
                  </div>
                  <div className="time-duration">
                    <i className="far fa-clock"></i> {t('consulting.steps.diagnostic.duration')}
                  </div>
                  <hr />
                  <ul className="feature-list">
                    {diagnosticFeatures.map((f) => (
                      <li key={f}>
                        <i className="fas fa-check"></i> {t(`consulting.steps.diagnostic.features.${f}`)}
                      </li>
                    ))}
                  </ul>
                  <p className="step-note">{t('consulting.steps.diagnostic.note')}</p>
                  <Button variant="primary" className="w-100 mt-3" onClick={scrollToBooking}>
                    {t('consulting.steps.diagnostic.cta')}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        <div className="examples-section">
          <h3 className="section-title text-center">{t('consulting.examplesTitle')}</h3>
          <Row className="justify-content-center">
            {['e1', 'e2', 'e3'].map((e) => (
              <Col md={4} className="mb-4" key={e}>
                <div className="example-block h-100">
                  <p>{t(`consulting.examples.${e}`)}</p>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        <div id="discovery-booking" className="free-booking-section">
          <h3 className="section-title">{t('consulting.freeBookingTitle')}</h3>
          <Row>
            <Col lg={8} className="mx-auto">
              <GoogleCalendarEmbed src={discoveryBookingLinks[currentLang]} />
            </Col>
          </Row>
        </div>

        <div className="faq-section">
          <h3 className="section-title">{t('consulting.faqTitle')}</h3>
          <Row>
            <Col lg={8} className="mx-auto">
              <div className="faq-item">
                <h5>{t('consulting.faq.question1')}</h5>
                <p>{t('consulting.faq.answer1')}</p>
              </div>
              <div className="faq-item">
                <h5>{t('consulting.faq.question2')}</h5>
                <p>{t('consulting.faq.answer2')}</p>
              </div>
              <div className="faq-item">
                <h5>{t('consulting.faq.question3')}</h5>
                <p>{t('consulting.faq.answer3')}</p>
              </div>
            </Col>
          </Row>
        </div>
      </Container>
    </section>
  );
}

export default Consulting;
